<template>
	<div class="accordion-item" :class="{ 'accordion-item--open': isOpen }">
		<button
			class="accordion-item__button"
			:aria-expanded="isOpen"
			@click="isOpen = !isOpen"
		>
			<div v-if="hasIconSlot" class="accordion-item__icon">
				<slot name="icon"></slot>
			</div>
			<div class="accordion-item__title">
				<slot name="title"></slot>
			</div>
			<span
				v-if="hasContentSlot"
				class="accordion-item__chevron"
				:class="{
					'accordion-item__chevron--open': isOpen && icon !== 'plus',
				}"
			>
				<SvgIcon :icon="iconState" />
			</span>
		</button>

		<transition
			v-if="hasContentSlot"
			name="expand"
			@enter="enter"
			@after-enter="afterEnter"
			@leave="leave"
		>
			<div v-show="isOpen" class="accordion-item__content">
				<div class="accordion-item__content-inner">
					<slot name="content"></slot>
				</div>
			</div>
		</transition>
	</div>
</template>

<script setup>
import { computed, ref, useSlots } from "vue";

const slots = useSlots();
const props = defineProps({
	ga4: { type: String, required: false, default: undefined },
	opened: { type: Boolean, required: false, default: false },
	icon: { type: String, required: false, default: "chevron" },
});

const isOpen = ref(props.opened);
const hasIconSlot = computed(() => !!slots.icon);
const hasContentSlot = computed(() => !!slots.content);
const iconState = computed(() => {
	if (props.icon === "plus") {
		if (isOpen.value) {
			return "minus";
		}
	}

	return props.icon;
});

function enter(element) {
	const width = getComputedStyle(element).width;

	element.style.width = width;
	element.style.position = "absolute";
	element.style.visibility = "hidden";
	element.style.height = "auto";

	const height = getComputedStyle(element).height;

	element.style.width = "";
	element.style.position = "";
	element.style.visibility = "";
	element.style.height = 0;

	// Force repaint to make sure the
	// animation is triggered correctly.
	getComputedStyle(element).height;

	// Trigger the animation.
	// We use `setTimeout` because we need
	// to make sure the browser has finished
	// painting after setting the `height`
	// to `0` in the line above.
	setTimeout(() => {
		element.style.height = height;
	});
}

function afterEnter(element) {
	element.style.height = "auto";
}

function leave(element) {
	const height = getComputedStyle(element).height;

	element.style.height = height;

	// Force repaint to make sure the
	// animation is triggered correctly.
	getComputedStyle(element).height;

	setTimeout(() => {
		element.style.height = 0;
	});
}
</script>

<style lang="scss">
.accordion-item {
	scroll-margin-top: calc(var(--header-height, 54px) + var(--spacing-2, 16px));

	* {
		will-change: height;
		backface-visibility: hidden;
	}

	.expand-enter-active,
	.expand-leave-active {
		transition: height 0.3s ease-in-out;
		overflow: hidden;
	}

	.expand-enter-from,
	.expand-leave-to {
		height: 0;
	}

	.accordion-item__container {
		background: transparent;
		margin: 0 auto;
		width: 100%;
	}

	.accordion-item__button {
		align-items: center;
		background: transparent;
		border: none;
		color: inherit;
		cursor: pointer;
		display: flex;
		font-size: var(--text-base, 16px);
		font-weight: 400;
		justify-content: space-between;
		margin: 0;
		position: relative;
		width: 100%;
		padding: 0 var(--spacing-2, 16px);

		&:hover,
		&:active {
			outline: none;
		}

		&:focus-visible {
			outline: 2px solid var(--color-btn-focus, #015ecc);
			outline-offset: 4px;
		}
	}

	.accordion-item__icon {
		width: 40px;
		height: 40px;
		display: flex;
		justify-content: center;
		align-items: center;

		img {
			padding: 0;
		}
	}

	.accordion-item__title {
		font-size: var(--text-lg, 20px);
		text-align: left;
		transition: all 0.5s;
		width: calc(100% - 112px);
		color: var(--color-white, #fff);
		font-weight: 300;
		line-height: 120%;
	}

	.accordion-item__chevron {
		display: flex;
		justify-content: center;
		align-items: center;
		pointer-events: none;
		transition: all 0.5s;
		width: 40px;
		height: 40px;
		z-index: 1;
		transform: rotate(180deg);

		svg {
			width: 100%;
		}

		path {
			fill: var(--color-white, #fff);
		}
	}

	.accordion-item__chevron--open {
		transform: rotate(0deg);
	}

	.accordion-item__content {
		color: var(--color-grey-4, #767676);
		font-size: var(--text-base, 16px);
		font-weight: 400;
		line-height: 1.2;
		text-align: left;
		max-width: calc(100% - 20px);
		padding-bottom: 0;

		&:before {
			content: "";
			display: block;
			padding-top: var(--spacing-3, 24px);
		}

		&:after {
			content: "";
			display: block;
			padding-bottom: var(--spacing-3, 24px);
		}

		> * {
			margin-bottom: 18px;

			&:last-child {
				margin-bottom: 0;
			}
		}

		a {
			color: inherit;
			text-decoration: none;
			transition: all 0.5s;
			border-bottom-width: 1px;
			border-bottom-style: solid;

			&:hover,
			&:focus {
				border-bottom-color: transparent;
			}

			&:visited {
				color: inherit;
			}
		}

		em,
		i {
			font-style: italic;
		}

		ul {
			padding-left: 40px;
		}

		ul li {
			list-style: disc;
		}

		ul li:not(:last-child),
		ol li:not(:last-child) {
			margin-bottom: 20px;
		}

		h4 {
			font-size: var(--text-lg, 20px);
			font-weight: 700;
			letter-spacing: 0;
			line-height: 1.2;
		}

		h5 {
			font-size: var(--text-base, 16px);
			font-weight: 700;
			letter-spacing: 0.025em;
			line-height: 1.2;
		}
	}

	.accordion-item__content-block {
		margin-bottom: var(--spacing-5, 40px);
	}

	.accordion-item__content-title {
		color: var(--color-white, #fff);
		font-weight: 700;
		line-height: 120%;
	}

	.accordion-item__content-link {
		font-weight: 700;
	}

	.accordion-item__transportation {
		margin-top: var(--spacing-3, 24px);
		display: flex;
		align-items: center;
		gap: var(--spacing-1, 8px);

		svg {
			width: 24px;
			height: 24px;
		}
	}

	.accordion-item__phone {
		display: flex;
		align-items: center;
		gap: var(--spacing-1, 8px);
	}
}
</style>
